import React, { useState, useEffect } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";

export default function ManagementDashboard() {
  const nav = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [showClassModal, setShowClassModal] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);

  // Data lists
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);

  // Question form
    const [qClassId, setQClassId] = useState("");
    const [qSubjectId, setQSubjectId] = useState("");
    const [questionText, setQuestionText] = useState("");
    const [optionA, setOptionA] = useState("");
    const [optionB, setOptionB] = useState("");
    const [optionC, setOptionC] = useState("");
    const [optionD, setOptionD] = useState("");
    const [correctAnswer, setCorrectAnswer] = useState("A");
  
    // Study content modal (separate state to avoid collision)
    const [showStudyContentModal, setShowStudyContentModal] = useState(false);
    const [studyTitle, setStudyTitle] = useState("");
    const [studyClassId, setStudyClassId] = useState("");
    const [studySubjectId, setStudySubjectId] = useState("");
    const [studyText, setStudyText] = useState("");
    const [studyFile, setStudyFile] = useState(null);
  
    // Practice set modal
    const [showPracticeSetModal, setShowPracticeSetModal] = useState(false);
    const [practiceName, setPracticeName] = useState("");
    const [practiceClassId, setPracticeClassId] = useState("");
    const [practiceSubjectId, setPracticeSubjectId] = useState("");
    const [practiceNumQuestions, setPracticeNumQuestions] = useState(10);
    const [practiceDuration, setPracticeDuration] = useState(600);
  
    // Scholarship exam modal
    const [showScholarshipModal, setShowScholarshipModal] = useState(false);
    const [scholarshipTitle, setScholarshipTitle] = useState("");
    const [scholarshipType, setScholarshipType] = useState("regular");
    const [scholarshipClassId, setScholarshipClassId] = useState("");
    const [scholarshipSubjectId, setScholarshipSubjectId] = useState("");
    const [scholarshipNumQuestions, setScholarshipNumQuestions] = useState(10);
    const [scholarshipDuration, setScholarshipDuration] = useState(600);
    const [scholarshipDate, setScholarshipDate] = useState("");
    const [scholarshipTime, setScholarshipTime] = useState("");
  

  // TOKEN CHECK + DATA LOAD
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.warn("No token found. Redirecting to login...");
      nav("/login");
      return;
    }

    loadData();
  }, [nav]);

  const loadData = () => {
    Promise.all([
      api.get('/admin/classes').catch(err => {
        console.error("Failed to load classes:", err.response?.data);
        return { data: [] };
      }),
      api.get('/admin/subjects').catch(err => {
        console.error("Failed to load subjects:", err.response?.data);
        return { data: [] };
      })
    ])
    .then(([classRes, subjectRes]) => {
      setClasses(classRes.data);
      setSubjects(subjectRes.data);
    });
  };

  function logout() {
    localStorage.removeItem("token");
    nav("/login");
  }

  // ADD CLASS
  const addClass = () => {
    const name = prompt("Enter Class Name (e.g., Class 10):");
    if (name) {
      api.post('/admin/classes', { name })
        .then(() => {
          alert("Class Added!");
          loadData();
          setShowClassModal(false);
        })
        .catch(err => {
          const msg = err.response?.data?.msg || err.response?.data?.error || "Error";
          alert(msg);
        });
    }
  };

  // ADD SUBJECT
  const addSubject = () => {
    const name = prompt("Enter Subject Name:");
    if (!name) return;
    const classId = prompt(`Enter Class ID (Available: ${classes.map(c => `${c.name}(${c.id})`).join(", ")}):`);
    if (classId) {
      api.post('/admin/subjects', { name, class_id: parseInt(classId) })
        .then(() => {
          alert("Subject Added!");
          loadData();
          setShowSubjectModal(false);
        })
        .catch(err => {
          const msg = err.response?.data?.msg || err.response?.data?.error || "Error";
          alert(msg);
        });
    }
  };

  // UPLOAD CSV
  const handleCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    api.post('/admin/upload_questions_csv', form)
      .then(() => {
        alert("Questions Uploaded Successfully!");
        setShowUploadModal(false);
      })
      .catch(err => {
        const msg = err.response?.data?.msg || err.response?.data?.error || "Upload Failed";
        alert(msg);
      });
  };

  // Manual add question
  const addQuestion = () => {
    if (!qClassId || !qSubjectId || !questionText) return alert("Please fill all fields");
    const payload = {
      question_type: "objective",
      text: questionText,
      option_a: optionA, option_b: optionB, option_c: optionC, option_d: optionD,
      correct: correctAnswer,
      class_id: parseInt(qClassId), subject_id: parseInt(qSubjectId)
    };
    api.post("/admin/questions", payload)
      .then(() => { alert("Question Added!"); setShowQuestionForm(false); setQuestionText(""); setOptionA(""); setOptionB(""); setOptionC(""); setOptionD(""); })
      .catch(err => alert(err.response?.data?.msg || err.response?.data?.error || "Error"));
  };

  // Helper to POST FormData and return promise
  const postFormData = (path, formData) => {
    // axios instance already has Authorization header logic and will set Content-Type correctly for FormData
    return api.post(path, formData, {
      headers: { "Content-Type": undefined } // let browser set boundary
    });
  };

  // Add Study Content
  const submitStudyContent = () => {
    if (!studyTitle || !studyClassId || !studySubjectId) return alert("Please fill title, class and subject");
    const fd = new FormData();
    fd.append("title", studyTitle);
    fd.append("class_id", studyClassId);
    fd.append("subject_id", studySubjectId);
    fd.append("content_text", studyText);
    if (studyFile) fd.append("file", studyFile);

    // Backend route expected: /api/admin/study-content
    postFormData("/admin/study-content", fd)
      .then(() => { alert("Study Content Added"); setShowStudyContentModal(false); setStudyTitle(""); setStudyText(""); setStudyFile(null); })
      .catch(err => {
        console.error(err);
        alert(err.response?.data?.msg || err.response?.data?.error || "Error adding study content");
      });
  };

  // Create Practice Set
  const createPracticeSet = () => {
  if (!practiceName || !practiceClassId || !practiceSubjectId)
    return alert("Please select class & subject");

  const payload = {
    name: practiceName,
    class_id: parseInt(practiceClassId),
    subject_id: parseInt(practiceSubjectId),
    num_questions: parseInt(practiceNumQuestions),
    duration_seconds: parseInt(practiceDuration)
  };

  api.post("/admin/practice_set", payload)
    .then(() => {
      alert("Practice Set Created");
      setShowPracticeSetModal(false);
    })
    .catch(err => {
      console.error(err);
      alert(err.response?.data?.error || "Error creating practice set");
    });
};

  // Create Scholarship Exam
  const createScholarshipExam = () => {
  if (
    !scholarshipTitle ||
    !scholarshipClassId ||
    !scholarshipSubjectId ||
    !scholarshipDate ||
    !scholarshipTime
  ) {
    return alert("Please fill all fields including class and subject");
  }

  const payload = {
    title: scholarshipTitle,
    exam_type: scholarshipType,
    class_id: parseInt(scholarshipClassId),
    subject_id: parseInt(scholarshipSubjectId),
    total_questions: parseInt(scholarshipNumQuestions),
    duration_seconds: parseInt(scholarshipDuration),
    exam_date: scholarshipDate,
    start_time: scholarshipTime
  };

  api.post("/admin/scholarship_exam", payload)
    .then(() => {
      alert("Scholarship Exam Created");
      setShowScholarshipModal(false);
    })
    .catch(err => {
      console.error(err);
      alert(err.response?.data?.error || "Error creating scholarship exam");
    });
};

  return (
    <div style={{ fontFamily: "Inter, sans-serif", background: "#f9fafb", minHeight: "100vh" }}>
      {/* Header */}
      <header style={headerStyle}>
        <h2 style={{ margin: 0, fontWeight: 600 }}>EduAt10 – Management</h2>
        <div style={{ position: "relative" }}>
          <div onClick={() => setShowMenu(!showMenu)} style={avatarStyle}>Profile</div>
          {showMenu && (
            <div style={menuStyle}>
              <button style={menuBtnStyle}>Profile</button>
              <button style={menuBtnStyle}>Subscription</button>
              <button onClick={logout} style={menuBtnStyle}>Logout</button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div style={{ padding: "40px" }}>
        <h3 style={{ color: "#1e293b" }}>Management Dashboard</h3>
        <p style={{ color: "#475569" }}>Manage classes, subjects, and monitor students’ performance.</p>

        <div style={cardGrid}>
          {/* Add Classes & Subjects */}
          <div style={cardStyle} onClick={() => setShowClassModal(true)}>Add Classes & Subjects</div>

          {/* Upload Questions */}
          <div style={cardStyle} onClick={() => setShowUploadModal(true)}>Upload Questions (CSV)</div>
          <div style={cardStyle} onClick={() => setShowQuestionForm(true)}>Add Question Manually</div>
          <div style={cardStyle} onClick={() => setShowStudyContentModal(true)}>Add Study Content</div>
          <div style={cardStyle} onClick={() => setShowPracticeSetModal(true)}>Create Practice Set</div>
          <div style={cardStyle} onClick={() => setShowScholarshipModal(true)}>Create Scholarship Exam</div>

          <div style={cardStyle}>Manage Users</div>

          {/* View Reports */}
          <div style={cardStyle}>View Reports</div>

          {/* Release Scholarships */}
          <div style={cardStyle}>Release Scholarships</div>
        </div>

        {/* MODALS */}
        {showClassModal && (
          <Modal title="Add Class / Subject" onClose={() => setShowClassModal(false)}>
            <button onClick={addClass} style={btnPrimary}>Add New Class</button>
            <button onClick={addSubject} style={btnSecondary}>Add Subject to Class</button>
          </Modal>
        )}

        {showUploadModal && (
          <Modal title="Upload Questions (CSV)" onClose={() => setShowUploadModal(false)}>
            <input type="file" accept=".csv" onChange={handleCSV} style={{ margin: "10px 0" }} />
            <p><small>CSV must have: type, class, subject, text, a, b, c, d, correct</small></p>
          </Modal>
        )}

        

      </div>
    </div>
  );
}

// Reusable Modal Component
const Modal = ({ title, children, onClose }) => (
  <div style={modalOverlay}>
    <div style={modalContent}>
      <h3>{title}</h3>
      <div style={{ margin: "20px 0" }}>{children}</div>
      <button onClick={onClose} style={btnClose}>Close</button>
    </div>
  </div>
);

// Styles
const headerStyle = {
  background: "#1e293b",
  color: "white",
  padding: "15px 30px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
};

const avatarStyle = {
  cursor: "pointer",
  background: "#334155",
  borderRadius: "50%",
  width: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 18,
  fontWeight: "bold",
};

const menuStyle = {
  position: "absolute",
  right: 0,
  marginTop: 10,
  background: "white",
  borderRadius: 8,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  overflow: "hidden",
  minWidth: 160,
  zIndex: 10,
};

const cardGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "20px",
  marginTop: "30px",
};

const cardStyle = {
  background: "white",
  padding: "25px",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  fontWeight: 500,
  textAlign: "center",
  cursor: "pointer",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
};

const menuBtnStyle = {
  display: "block",
  width: "100%",
  padding: "10px 15px",
  border: "none",
  background: "white",
  textAlign: "left",
  cursor: "pointer",
  color: "#1e293b",
  fontSize: 15,
};

const modalOverlay = {
  position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
  background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000
};

const modalContent = {
  background: "white", padding: 30, borderRadius: 12, width: 500, maxWidth: "90%", maxHeight: "80vh", overflowY: "auto"
};

const btnPrimary = { background: "#1e293b", color: "white", padding: "10px 20px", border: "none", borderRadius: 6, cursor: "pointer", margin: "5px" };
const btnSecondary = { background: "#475569", color: "white", padding: "10px 20px", border: "none", borderRadius: 6, cursor: "pointer", margin: "5px" };
const btnClose = { background: "#ef4444", color: "white", padding: "10px 20px", border: "none", borderRadius: 6, cursor: "pointer", margin: "5px" };